/**
 * Artifact bulk-search descriptor — adapts SearchArtifacts to BulkSearchResults.
 * The queried ids go in as artifactIds; status/state/type are server-side
 * PropertyFilter refinements (type via additionalFilters.artifactType).
 */
import Badge from '@cloudscape-design/components/badge';
import { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import { titleCase, fullTimestamp } from 'src/store/slices/paymentDetail/paymentDetailFormat';
import { formatLatency } from 'src/utils/formatters';
import { DetailLink } from 'src/components/common/DetailLink';
import {
  ApiArtifactSummary,
  ArtifactFilters,
  SearchArtifactsInput,
} from 'src/store/slices/searchArtifacts/searchArtifactsApi';
import { searchArtifacts, clearSearchArtifacts } from 'src/store/slices/searchArtifacts/searchArtifactsSlice';
import {
  BulkSearchArgs,
  BulkSearchDescriptor,
  StatusCell,
  dash,
  firstOf,
} from './bulkSearchShared';

const FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] = [
  { key: 'status', propertyLabel: 'Status', groupValuesLabel: 'Status values', operators: ['='] },
  { key: 'state', propertyLabel: 'State', groupValuesLabel: 'State values', operators: ['='] },
  { key: 'type', propertyLabel: 'Type', groupValuesLabel: 'Type values', operators: ['='] },
];

const buildInput = ({ ids, pageNumber, pageSize, filters }: BulkSearchArgs): SearchArtifactsInput => {
  const f: ArtifactFilters = { artifactIds: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconArtifactStatus = status;
  if (state) f.beaconArtifactState = state;
  if (filters.type?.length) f.additionalFilters = { artifactType: filters.type };
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const artifactSearchDescriptor: BulkSearchDescriptor<ApiArtifactSummary> = {
  entity: 'artifact',
  title: 'Bulk artifact search',
  path: '/artifacts',
  filteringProperties: FILTERING_PROPERTIES,
  columns: [
    {
      id: 'id',
      header: 'Artifact id',
      cell: (a) => <DetailLink to={`/artifact?id=${encodeURIComponent(a.id)}`}>{a.id}</DetailLink>,
    },
    { id: 'artifactType', header: 'Type', cell: (a) => (a.artifactType ? <Badge>{a.artifactType}</Badge> : '-') },
    { id: 'beaconStatus', header: 'Beacon status', cell: (a) => <StatusCell state={a.beaconArtifactState} label={a.beaconArtifactStatus} /> },
    { id: 'statusCode', header: 'Status code', cell: (a) => dash(a.statusCode) },
    { id: 'system', header: 'System', cell: (a) => dash(a.currentSystem) },
    { id: 'direction', header: 'Direction', cell: (a) => (a.currentDirection ? titleCase(a.currentDirection) : '-') },
    { id: 'payments', header: 'Payments', cell: (a) => a.paymentIds?.length ?? 0 },
    { id: 'executions', header: 'Executions', cell: (a) => a.executionCount ?? 0 },
    { id: 'totalLatency', header: 'Total latency', cell: (a) => formatLatency(a.totalLatencyMs) },
    { id: 'updatedAt', header: 'Updated', cell: (a) => (a.updatedAt ? fullTimestamp(a.updatedAt) : '-') },
  ],
  run: (dispatch, args) => { dispatch(searchArtifacts(buildInput(args))); },
  clear: (dispatch) => { dispatch(clearSearchArtifacts()); },
  select: (s) => ({
    items: s.searchArtifacts.artifacts,
    total: s.searchArtifacts.total,
    loading: s.searchArtifacts.loading,
    error: s.searchArtifacts.error,
    regionResults: s.searchArtifacts.regionResults,
    searchedRegions: s.searchArtifacts.searchedRegions,
    regionErrors: s.searchArtifacts.regionErrors,
  }),
};
